import { useCallback, useMemo, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { Input } from '@/lib/base/forms/input/input';
import { Dialog, Modal, ModalOverlay } from '@/lib/base/overlays/modal';
import { useUpdateCrmProperty } from '@/lib/crm/hooks/useUpdateCrmProperty';
import { logger } from '@/lib/logger';
import PlateEmailEditor from '@/lib/reasoning/components/email-editor/PlateEmailEditor';

import type {
  CannedResponsesData,
  CannedResponseWithSource,
} from './useCannedResponses';
import { CANNED_RESPONSES_PROPERTY } from './useCannedResponses';

export interface ManageCannedResponsesModalProps {
  orgId: number;
  userId: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  data: CannedResponsesData;
}

interface Draft {
  /** id of the response being edited, or null for a new response. */
  responseId: string | null;
  /** Entity the response lives on (primary entity for new responses). */
  entityId: string;
  name: string;
  html: string;
}

/**
 * Management modal for canned responses: list, add, edit, delete.
 *
 * New responses are written to the user's primary email integration entity;
 * edits and deletes are written back to the entity the response lives on
 * (write-to-primary / read-the-union).
 */
export default function ManageCannedResponsesModal({
  orgId,
  userId,
  isOpen,
  onOpenChange,
  data,
}: ManageCannedResponsesModalProps) {
  const { responses, primaryEntityId, objectsById } = data;
  const [draft, setDraft] = useState<Draft | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const targetEntityId = draft?.entityId ?? primaryEntityId;
  const targetObject = targetEntityId
    ? (objectsById[targetEntityId] ?? null)
    : null;
  const updateProperty = useUpdateCrmProperty(orgId, userId);

  const targetList = useMemo(
    () => responses.filter(r => r.entityId === targetEntityId),
    [responses, targetEntityId]
  );

  const handleNew = useCallback(() => {
    if (!primaryEntityId) {
      return;
    }
    setServerError(null);
    setDraft({
      responseId: null,
      entityId: primaryEntityId,
      name: '',
      html: '',
    });
  }, [primaryEntityId]);

  const handleEdit = useCallback((response: CannedResponseWithSource) => {
    setServerError(null);
    setDraft({
      responseId: response.id,
      entityId: response.entityId,
      name: response.name,
      html: response.html,
    });
  }, []);

  const handleNameChange = useCallback((name: string) => {
    setDraft(current => (current ? { ...current, name } : current));
  }, []);

  const handleHtmlChange = useCallback((html: string) => {
    setDraft(current => (current ? { ...current, html } : current));
  }, []);

  const persistList = useCallback(
    (nextList: CannedResponseWithSource[], onDone?: () => void) => {
      if (!targetObject) {
        setServerError('Email integration is still loading. Try again.');
        return;
      }
      setIsSaving(true);
      setServerError(null);
      updateProperty.mutate(
        {
          object: targetObject,
          propertyName: CANNED_RESPONSES_PROPERTY,
          value: nextList.map(({ id, name, html }) => ({ id, name, html })),
        },
        {
          onSuccess: () => {
            onDone?.();
          },
          onError: (err: unknown) => {
            setServerError("We couldn't save this canned response. Try again.");
            logger.error(
              'Canned response save failed',
              err instanceof Error ? err : new Error(String(err)),
              {
                component: 'emails',
                property: CANNED_RESPONSES_PROPERTY,
                errorType:
                  err instanceof Error ? err.constructor.name : 'Unknown',
              }
            );
          },
          onSettled: () => setIsSaving(false),
        }
      );
    },
    [updateProperty, targetObject]
  );

  const handleSave = useCallback(() => {
    if (!draft || !targetEntityId) {
      return;
    }
    const entry: CannedResponseWithSource = {
      id: draft.responseId ?? crypto.randomUUID(),
      name: draft.name.trim(),
      html: draft.html,
      entityId: targetEntityId,
    };
    const nextList = draft.responseId
      ? targetList.map(r => (r.id === draft.responseId ? entry : r))
      : [...targetList, entry];
    persistList(nextList, () => setDraft(null));
  }, [draft, targetEntityId, targetList, persistList]);

  const handleDelete = useCallback(() => {
    if (!draft?.responseId) {
      return;
    }
    persistList(
      targetList.filter(r => r.id !== draft.responseId),
      () => setDraft(null)
    );
  }, [draft, targetList, persistList]);

  const handleBack = useCallback(() => {
    setServerError(null);
    setDraft(null);
  }, []);

  // Reset modal-local state on close so reopening always lands on the
  // templates list rather than a stale edit form or error from last session.
  const handleOpenChange = useCallback(
    (open: boolean) => {
      // A save in flight owns the modal: ignore dismiss attempts (Escape,
      // backdrop click) until it settles. Otherwise the reset below runs,
      // then the mutation's late onError restores serverError onto an
      // already-closed modal — resurfacing as a stale error on next open.
      if (!open && isSaving) {
        return;
      }
      if (!open) {
        setDraft(null);
        setServerError(null);
      }
      onOpenChange(open);
    },
    [onOpenChange, isSaving]
  );

  const canSave = Boolean(
    draft && draft.name.trim() && !isSaving && targetObject
  );

  // CTAs live in the Dialog's sticky footer region (per the modal pattern),
  // varying by mode: list mode offers "New template"; edit mode offers
  // Delete (existing templates only) / Cancel / Save.
  const footer =
    draft === null ? (
      <div className='flex justify-end'>
        <Button
          color='primary'
          size='sm'
          onClick={handleNew}
          isDisabled={!primaryEntityId || !targetObject}
        >
          New template
        </Button>
      </div>
    ) : (
      <div className='flex items-center justify-between'>
        <div>
          {draft.responseId && (
            <Button
              color='primary-destructive'
              size='sm'
              onClick={handleDelete}
              isDisabled={isSaving}
            >
              Delete
            </Button>
          )}
        </div>
        <div className='flex gap-2'>
          <Button
            color='secondary'
            size='sm'
            onClick={handleBack}
            isDisabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            color='primary'
            size='sm'
            onClick={handleSave}
            isDisabled={!canSave}
          >
            {isSaving ? 'Saving…' : 'Save template'}
          </Button>
        </div>
      </div>
    );

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={handleOpenChange}>
      <Modal className='max-w-2xl w-full'>
        <Dialog
          aria-label='Manage email templates'
          title='Email templates'
          description='Reusable emails you can insert when composing.'
          instance='manage-email-templates'
          footer={footer}
        >
          <div className='space-y-4 pb-2'>
            {serverError && (
              <p role='alert' className='text-xs text-error-600'>
                {serverError}
              </p>
            )}
            {draft === null ? (
              <ul role='list' className='space-y-1'>
                {responses.length === 0 && (
                  <li className='text-sm text-fg-tertiary'>
                    No templates yet.
                  </li>
                )}
                {responses.map(response => (
                  <CannedResponseRow
                    key={`${response.entityId}:${response.id}`}
                    response={response}
                    onEdit={handleEdit}
                  />
                ))}
              </ul>
            ) : (
              <div className='space-y-3'>
                <Input
                  label='Name'
                  value={draft.name}
                  onChange={handleNameChange}
                  placeholder='e.g. Intro follow-up'
                  isDisabled={isSaving}
                />
                <div className='border border-border-secondary rounded-lg bg-bg-primary'>
                  <PlateEmailEditor
                    value={draft.html}
                    onChange={handleHtmlChange}
                    placeholder='Write the template'
                    orgId={orgId}
                    readOnly={isSaving}
                  />
                </div>
              </div>
            )}
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}

interface CannedResponseRowProps {
  response: CannedResponseWithSource;
  onEdit: (response: CannedResponseWithSource) => void;
}

function CannedResponseRow({ response, onEdit }: CannedResponseRowProps) {
  const handleClick = useCallback(() => onEdit(response), [onEdit, response]);
  return (
    <li>
      <button
        type='button'
        onClick={handleClick}
        className='w-full text-left px-3 py-2 text-sm rounded-lg border border-border-secondary hover:bg-bg-secondary text-primary'
      >
        {response.name}
      </button>
    </li>
  );
}
